import React, { useState, useEffect, useRef } from 'react';
import { Table, Tabs, Row, Col, Statistic, Button, Tooltip } from 'antd';
import { useUserInfo } from '../../hooks/user-info';
import { ErrorText, Loading } from '../../components';
import { ColumnSwitcherator, rawEmailInputToEmailArray, transformSelectedUserToUserBaseTableData, transformSelectedUserToUserTeamTableData, transformSelectedUserToUserMetaDataTableData, transformUserInfoDataToMainTableData, validateEmailArray, } from './helpers';
import { mainTableColumns, userBaseTableColumns, userMetaDataTableColumns, userTeamTableColumns } from './columns';
import { VerticalOffset, EmailInput, EmailInputError } from './styled';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(advancedFormat);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
const { TabPane } = Tabs;
export const Userinfo = () => {
    const [inputValue, setInputValue] = useState('');
    const { userInfoFetching, userInfoError, userInfoData, getUserInfo } = useUserInfo();
    const [selectedUser, setSelectedUser] = useState();
    const [displayedUsers, setDisplayedUsers] = useState(0);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [dataAge, setDataAge] = useState(' ');
    const intervalTimerRef = useRef();
    const [visibleColumns, setVisibleColumns] = useState([
        'email',
        'firstName',
        'lastName',
    ]);
    const [inputError, setInputError] = useState('');
    const mainTableData = transformUserInfoDataToMainTableData(userInfoData);
    const userBaseTableData = transformSelectedUserToUserBaseTableData(selectedUser);
    const userTeamTableData = transformSelectedUserToUserTeamTableData(selectedUser);
    const userMetaDataTableData = transformSelectedUserToUserMetaDataTableData(selectedUser);
    useEffect(() => {
        if (userInfoData && !userInfoFetching && !userInfoError) {
            setDisplayedUsers(Object.keys(userInfoData.users).length);
            if (intervalTimerRef.current) {
                clearInterval(intervalTimerRef.current);
            }
            intervalTimerRef.current = setInterval(() => {
                setDataAge(dayjs(userInfoData.now).fromNow());
            }, 10000);
        }
    }, [userInfoData]);
    const loadAllData = () => {
        setInputError('');
        setInputValue('');
        getUserInfo(['*']).catch((e) => {
            console.error(`Unexpected error in getUserInfo function in UserInfo tab component in support tool web app: ${e.message}`);
        });
    };
    const loadData = (emails) => {
        setSelectedUser(undefined);
        setSelectedRowKeys([]);
        setDataAge(' ');
        const inputString = emails ? emails : inputValue;
        const emailArray = rawEmailInputToEmailArray(inputString);
        if (validateEmailArray(emailArray)) {
            getUserInfo(emailArray).catch((e) => {
                console.error(`Unexpected error in getUserInfo function in UserInfo tab component in support tool web app: ${e.message}`);
            });
            setInputError('');
        }
        else {
            setInputError('Invalid Email(s) Entered');
        }
        setInputValue(emailArray.join(' '));
    };
    const onChangeHandler = (event) => {
        setInputValue(event.currentTarget.value);
    };
    const onKeyUpHandler = (event) => {
        const key = event.key || event.keyCode;
        const inputValue = event.currentTarget.value.trim();
        if (key === 'Enter' || key === 13) {
            if (inputValue) {
                loadData();
            }
            else {
                setInputValue('');
            }
        }
    };
    const rowSelection = {
        selectedRowKeys,
        type: 'radio',
    };
    const columnSwitchCallback = (columnName, isEnabled) => {
        const columns = visibleColumns.filter((e) => e !== columnName);
        setVisibleColumns(isEnabled ? [...columns, columnName] : [...columns]);
    };
    return (React.createElement("div", null,
        React.createElement("h1", null, "Check User Info"),
        React.createElement(Row, null,
            React.createElement(Col, { span: 8 },
                React.createElement(Row, { gutter: 16 },
                    React.createElement(Col, null,
                        React.createElement(EmailInputError, { errorText: inputError }, inputError),
                        React.createElement(Tooltip, { trigger: ['hover'], title: "You can enter comma or space separated emails in here", placement: "topRight" },
                            React.createElement(EmailInput, { onChange: onChangeHandler, onKeyUp: onKeyUpHandler, value: inputValue })))),
                React.createElement(VerticalOffset, { pixels: 8 },
                    React.createElement(Row, { gutter: 16 },
                        React.createElement(Col, null,
                            React.createElement(Button, { onClick: () => inputValue.trim() && loadData() }, "Get Selected Users")),
                        React.createElement(Col, null,
                            React.createElement(Button, { onClick: () => loadAllData() }, "Get All Users"))))),
            React.createElement(Col, { span: 16 },
                React.createElement(Row, null,
                    React.createElement(Col, { span: 4 },
                        React.createElement(VerticalOffset, { pixels: -28 },
                            React.createElement(Statistic, { title: "Displayed / Total Users", value: ((100 * displayedUsers) / mainTableData.length || 0).toFixed(2), suffix: "%", loading: userInfoFetching }))),
                    React.createElement(Col, { span: 4 },
                        React.createElement(VerticalOffset, { pixels: -28 },
                            React.createElement(Statistic, { title: "Displayed Users", value: displayedUsers, loading: userInfoFetching }))),
                    React.createElement(Col, { span: 4 },
                        React.createElement(VerticalOffset, { pixels: -28 },
                            React.createElement(Statistic, { title: "Total Users", value: mainTableData.length, loading: userInfoFetching }))),
                    React.createElement(Col, null,
                        React.createElement(VerticalOffset, { pixels: -28 },
                            React.createElement(Statistic, { title: "Time Received", value: userInfoData
                                    ? `${dayjs(userInfoData.now).format('h:mm:ss.SSSa ddd DD/MM/YY')} ${dayjs.tz.guess()}`
                                    : ' ', loading: userInfoFetching })))))),
        React.createElement(VerticalOffset, { pixels: 16 },
            React.createElement(Row, null,
                React.createElement(Col, { span: 8 },
                    React.createElement(Row, { gutter: 16 },
                        React.createElement(Col, null,
                            React.createElement(ColumnSwitcherator, { columnName: "uri", callback: columnSwitchCallback })),
                        React.createElement(Col, null,
                            React.createElement(ColumnSwitcherator, { columnName: "email", callback: columnSwitchCallback, checked: true })),
                        React.createElement(Col, null,
                            React.createElement(ColumnSwitcherator, { columnName: "firstName", callback: columnSwitchCallback, checked: true })),
                        React.createElement(Col, null,
                            React.createElement(ColumnSwitcherator, { columnName: "lastName", callback: columnSwitchCallback, checked: true }))),
                    React.createElement(VerticalOffset, { pixels: 8 },
                        React.createElement(Row, { gutter: 16 },
                            React.createElement(Col, null,
                                React.createElement(ColumnSwitcherator, { columnName: "initials", callback: columnSwitchCallback })),
                            React.createElement(Col, null,
                                React.createElement(ColumnSwitcherator, { columnName: "avatarImgUrl", callback: columnSwitchCallback })),
                            React.createElement(Col, null,
                                React.createElement(ColumnSwitcherator, { columnName: "fill", callback: columnSwitchCallback })),
                            React.createElement(Col, null,
                                React.createElement(ColumnSwitcherator, { columnName: "stroke", callback: columnSwitchCallback }))))),
                React.createElement(Col, { span: 16 },
                    React.createElement(Row, null,
                        React.createElement(Col, { offset: 12 },
                            React.createElement(Statistic, { title: "Data Age", value: dataAge, loading: userInfoFetching })))))),
        userInfoFetching && React.createElement(Loading, null),
        !userInfoFetching && userInfoError && (React.createElement(VerticalOffset, { pixels: 32 },
            React.createElement(ErrorText, null, userInfoError.message))),
        !userInfoFetching && userInfoData && (React.createElement(VerticalOffset, { pixels: 16 },
            React.createElement(Row, { gutter: 16 },
                React.createElement(Col, { span: 16 },
                    React.createElement(Table, { rowSelection: Object.assign({}, rowSelection), dataSource: mainTableData, columns: mainTableColumns.filter((column) => visibleColumns.includes(column['dataIndex'])), rowKey: "uri", onChange: (pagination, filters, sorter, { currentDataSource, action }) => {
                            if (action === 'filter') {
                                setDisplayedUsers(currentDataSource.length);
                                setSelectedUser(undefined);
                                setSelectedRowKeys([]);
                            }
                        }, onRow: (record) => ({
                            onClick: () => {
                                setSelectedRowKeys([record.uri]);
                                setSelectedUser(userInfoData.users[record.uri]);
                            },
                        }), pagination: {
                            position: ['topRight'],
                            showQuickJumper: true,
                            showSizeChanger: true,
                            pageSizeOptions: ['5', '10', '20', '50', '100'],
                        } })),
                React.createElement(Col, { span: 8 },
                    React.createElement(VerticalOffset, { pixels: 8 },
                        React.createElement(Tabs, { type: "card" },
                            React.createElement(TabPane, { tab: "User", key: "1" },
                                React.createElement(VerticalOffset, { pixels: selectedUser ? -64 : 0 },
                                    React.createElement(Table, { dataSource: userBaseTableData, columns: userBaseTableColumns, pagination: {
                                            position: ['topRight'],
                                            showQuickJumper: true,
                                            showSizeChanger: true,
                                            pageSizeOptions: ['5', '10', '20', '50', '100'],
                                        } }))),
                            React.createElement(TabPane, { tab: "Team", key: "2" },
                                React.createElement(VerticalOffset, { pixels: selectedUser ? -64 : 0 },
                                    React.createElement(Table, { dataSource: userTeamTableData, columns: userTeamTableColumns, pagination: {
                                            position: ['topRight'],
                                            showQuickJumper: true,
                                            showSizeChanger: true,
                                            pageSizeOptions: ['5', '10', '20', '50', '100'],
                                        } }))),
                            React.createElement(TabPane, { tab: "Metadata", key: "3" },
                                React.createElement(VerticalOffset, { pixels: selectedUser ? -64 : 0 },
                                    React.createElement(Table, { dataSource: userMetaDataTableData, columns: userMetaDataTableColumns, pagination: {
                                            position: ['topRight'],
                                            showQuickJumper: true,
                                            showSizeChanger: true,
                                            pageSizeOptions: ['5', '10', '20', '50', '100'],
                                        } })))))))))));
};
//# sourceMappingURL=index.js.map